import { useEffect, RefObject, useState, useCallback, useRef } from 'react';
import { useStore } from '@xyflow/react';

const zoomSelector = (s) => s.transform[2];

export const useDrawLines = (
  areActionsExpanded: boolean,
  expandButtonRef: RefObject<HTMLDivElement>,
  actionRefs: RefObject<(HTMLDivElement | null)[]>,
  containerRef: RefObject<HTMLDivElement>,
  iconWrapperClassName: string,
  animationDuration: number = 300,
): string[] => {
  const [paths, setPaths] = useState<string[]>([]);
  const wasExpanded = useRef(false);
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const zoom = useStore(zoomSelector);
  const preCalculatedPaths = useRef<string[]>([]);
  const isInitialized = useRef(false);
  const lastValidDimensions = useRef<{ width: number; height: number } | null>(null);
  const isFirstRenderAfterSelection = useRef(true);
  const initializationAttempts = useRef(0);
  const MAX_INIT_ATTEMPTS = 5;

  const hasValidDimensions = useCallback(() => {
    if (!containerRef.current || !expandButtonRef.current) {
      return false;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const expandButtonRect = expandButtonRef.current.getBoundingClientRect();

    // Check if container has a valid width and is visible
    const isValid =
      containerRect.width > 0 &&
      expandButtonRect.width > 0 &&
      containerRect.right > containerRect.left;

    if (isValid) {
      lastValidDimensions.current = {
        width: containerRect.width,
        height: containerRect.height,
      };
    }

    return isValid;
  }, [containerRef, expandButtonRef]);

  const calculatePaths = useCallback(() => {
    if (!areActionsExpanded && preCalculatedPaths.current.length > 0) {
      return preCalculatedPaths.current;
    }

    if (!hasValidDimensions()) {
      return [];
    }

    const containerRect = containerRef.current!.getBoundingClientRect();
    const expandButtonRect = expandButtonRef.current!.getBoundingClientRect();

    const zoomLevel = zoom;

    const expandButtonPoint = {
      x: Math.max(0, (expandButtonRect.right - containerRect.left) / zoomLevel),
      y: (expandButtonRect.top - containerRect.top + expandButtonRect.height / 2) / zoomLevel,
    };

    const calculatedPaths = actionRefs.current.map((actionRef, index) => {
      if (!actionRef) {
        return '';
      }

      const iconWrapper = actionRef.querySelector(`.${iconWrapperClassName}`);
      if (!iconWrapper) {
        return '';
      }

      const iconRect = iconWrapper.getBoundingClientRect();

      const actionPoint = {
        x: (iconRect.left - containerRect.left + iconRect.width / 2) / zoomLevel,
        y: (iconRect.top - containerRect.top + iconRect.height / 2) / zoomLevel,
      };

      const dx = actionPoint.x - expandButtonPoint.x;
      const dy = actionPoint.y - expandButtonPoint.y;

      const controlPoint1 = {
        x: expandButtonPoint.x + dx * 0.25,
        y: expandButtonPoint.y,
      };

      const controlPoint2 = {
        x: actionPoint.x - dx * 0.25,
        y: actionPoint.y,
      };

      return `M ${expandButtonPoint.x} ${expandButtonPoint.y} 
              C ${controlPoint1.x} ${controlPoint1.y}, 
                ${controlPoint2.x} ${controlPoint2.y}, 
                ${actionPoint.x} ${actionPoint.y}`;
    });

    preCalculatedPaths.current = calculatedPaths;

    return calculatedPaths;
  }, [
    areActionsExpanded,
    expandButtonRef,
    actionRefs,
    containerRef,
    iconWrapperClassName,
    zoom,
    hasValidDimensions,
  ]);

  // Initialize paths when component mounts
  useEffect(() => {
    if (isInitialized.current) {
      return;
    }

    const initializePaths = () => {
      if (hasValidDimensions()) {
        const initialPaths = calculatePaths();
        if (initialPaths.length > 0) {
          preCalculatedPaths.current = initialPaths;
          isInitialized.current = true;
        }
      } else {
        initializationAttempts.current += 1;
      }
    };

    initializePaths();

    // If dimensions aren't ready and we haven't exceeded max attempts, set up a ResizeObserver
    if (
      !isInitialized.current &&
      containerRef.current &&
      initializationAttempts.current < MAX_INIT_ATTEMPTS
    ) {
      const observer = new ResizeObserver(() => {
        initializePaths();
        if (isInitialized.current || initializationAttempts.current >= MAX_INIT_ATTEMPTS) {
          observer.disconnect();
        }
      });

      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }
  }, [calculatePaths, hasValidDimensions]);

  // Handle expansion state changes
  useEffect(() => {
    if (!areActionsExpanded) {
      if (wasExpanded.current) {
        setPaths([]);
        wasExpanded.current = false;
      }
      return;
    }

    if (areActionsExpanded && wasExpanded.current) {
      return;
    }

    wasExpanded.current = true;
    isFirstRenderAfterSelection.current = false;

    if (containerRef.current) {
      resizeObserver.current = new ResizeObserver(() => {
        if (hasValidDimensions()) {
          const newPaths = calculatePaths();
          if (newPaths.length > 0) {
            setPaths(newPaths);
          }
        }
      });

      resizeObserver.current.observe(containerRef.current);
    }

    // Initial calculation after a small delay to ensure dimensions are ready
    const initialTimeoutId = setTimeout(() => {
      if (areActionsExpanded && hasValidDimensions()) {
        const initialPaths = calculatePaths();
        setPaths(initialPaths);
      }
    }, 150);

    // Recalculate after animation completes
    const animationTimeoutId = setTimeout(() => {
      if (areActionsExpanded && hasValidDimensions()) {
        const newPaths = calculatePaths();
        setPaths(newPaths);
      }
    }, animationDuration + 100);

    return () => {
      clearTimeout(initialTimeoutId);
      clearTimeout(animationTimeoutId);
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
      }
    };
  }, [areActionsExpanded, calculatePaths, animationDuration, hasValidDimensions]);

  // Handle zoom changes
  useEffect(() => {
    if (areActionsExpanded && hasValidDimensions()) {
      const newPaths = calculatePaths();
      setPaths(newPaths);
    }
  }, [zoom, areActionsExpanded, calculatePaths, hasValidDimensions]);

  return paths;
};
